/**
 * DOM-side draggable bone debug panel.
 *
 * Reads per-frame bone rotation data from window.__poseref_boneDebug
 * (written by BoneDebugDataPump in BoneOverlay.tsx) and renders it
 * in a DraggablePanel. Only active when debug mode is enabled.
 */

import { useCallback, useEffect, useRef } from "react";
import { useStore } from "../app/store.ts";
import { CopyButton } from "../ui/CopyButton.tsx";
import { DraggablePanel } from "../ui/DraggablePanel.tsx";
import { usePanelSelection } from "../ui/floatingPanelStore.ts";
import { Z_DEBUG } from "../ui/tokens.ts";
import type { BoneDebugSnapshot } from "./BoneOverlay.tsx";

export function BoneDebugPanel() {
  const debugEnabled = useStore((s) => s.debugEnabled);
  const panelOpen = useStore((s) => s.debugPanelOpen.boneInfo);
  const poseMode = useStore((s) => s.poseMode);
  const togglePanel = useStore((s) => s.toggleDebugPanel);
  const sel = usePanelSelection("debug-bone-info", () =>
    togglePanel("boneInfo"),
  );
  const contentRef = useRef<HTMLDivElement>(null);
  const visibleRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!debugEnabled || !panelOpen || !poseMode) return;

    let raf: number;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const snap = window.__poseref_boneDebug;
      const wrapper = wrapperRef.current;
      if (!wrapper) return;

      if (!snap) {
        if (visibleRef.current) {
          wrapper.style.display = "none";
          visibleRef.current = false;
        }
        return;
      }

      if (!visibleRef.current) {
        wrapper.style.display = "";
        visibleRef.current = true;
      }

      const el = contentRef.current;
      if (!el) return;

      el.innerHTML = buildContent(snap);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [debugEnabled, panelOpen, poseMode]);

  const getPlainText = useCallback(() => {
    const snap = window.__poseref_boneDebug;
    if (!snap) return "";
    return buildPlainText(snap);
  }, []);

  if (!debugEnabled || !panelOpen || !poseMode) return null;

  return (
    <div ref={wrapperRef} style={{ display: "none" }}>
      <DraggablePanel
        id="debug-bone-info"
        defaultPosition={{ top: 8, left: 8 }}
        zIndex={Z_DEBUG}
        collapsible
        selectionColor="#ff0"
        {...sel}
        style={{ minWidth: 260 }}
        header={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              fontFamily: "monospace",
              fontSize: 11,
            }}
          >
            <span style={{ color: "#40d8ff", fontWeight: "bold" }}>
              BONE INFO
            </span>
            <CopyButton getText={getPlainText} />
          </div>
        }
      >
        <div
          style={{
            background: "rgba(0,0,0,0.85)",
            color: "#fff",
            fontFamily: "monospace",
            fontSize: 11,
            padding: "6px 10px",
            borderRadius: "0 0 6px 6px",
            pointerEvents: "auto",
            lineHeight: 1.5,
            whiteSpace: "pre",
          }}
        >
          <div ref={contentRef} />
        </div>
      </DraggablePanel>
    </div>
  );
}

/** Pick color based on highest percentage in a saturation string like "cone:85% twist:23%". */
function satColor(sat: string): string {
  const pcts = [...sat.matchAll(/(\d+)%/g)].map((m) => parseInt(m[1], 10));
  const max = Math.max(0, ...pcts);
  if (max >= 90) return "#ff4444";
  if (max >= 70) return "#ff8844";
  if (max >= 50) return "#ffcc44";
  return "#88cc88";
}

function buildPlainText(s: BoneDebugSnapshot): string {
  return [
    `${s.boneName}  parent: ${s.parentName}`,
    `Local   X:${s.localDeg[0]}°  Y:${s.localDeg[1]}°  Z:${s.localDeg[2]}°`,
    `World   X:${s.worldDeg[0]}°  Y:${s.worldDeg[1]}°  Z:${s.worldDeg[2]}°`,
    s.restDeg
      ? `FromRest X:${s.restDeg[0]}°  Y:${s.restDeg[1]}°  Z:${s.restDeg[2]}°`
      : null,
    s.twist
      ? `Twist   bone:${s.twist.bone}°  parent:${s.twist.parent}°  Δ${s.twist.delta}°`
      : null,
    s.cone != null ? `Cone    ${s.cone}°` : null,
    s.saturation ? `Limits  ${s.saturation}` : null,
    s.symmetry
      ? `Sym Δ   X:${s.symmetry.deltaXYZ[0]}°  Y:${s.symmetry.deltaXYZ[1]}°  Z:${s.symmetry.deltaXYZ[2]}°  Σ${s.symmetry.total}°  vs ${s.symmetry.mirror}`
      : null,
    `Quat: [${s.quat.join(", ")}]`,
    `Stored: ${s.hasStoredPose ? "yes" : "rest pose"}`,
    `LocalX: [${s.localX.join(", ")}]`,
    `LocalY: [${s.localY.join(", ")}]`,
    `LocalZ: [${s.localZ.join(", ")}]`,
  ]
    .filter(Boolean)
    .join("\n");
}

function buildContent(s: BoneDebugSnapshot): string {
  // Use colored spans for the axis labels
  return [
    `<span style="color:#40d8ff;font-weight:bold">${s.boneName}</span>` +
      `<span style="color:#999"> parent: ${s.parentName}</span>`,
    `<span style="color:#e8a030">Local  </span>` +
      `<span style="color:#ff6666">X:${s.localDeg[0]}°</span> ` +
      `<span style="color:#66ff66">Y:${s.localDeg[1]}°</span> ` +
      `<span style="color:#6688ff">Z:${s.localDeg[2]}°</span>`,
    `<span style="color:#e8a030">World  </span>` +
      `<span style="color:#ff6666">X:${s.worldDeg[0]}°</span> ` +
      `<span style="color:#66ff66">Y:${s.worldDeg[1]}°</span> ` +
      `<span style="color:#6688ff">Z:${s.worldDeg[2]}°</span>`,
    s.restDeg
      ? `<span style="color:#e8a030">FromRest</span>` +
        ` <span style="color:#ff6666">X:${s.restDeg[0]}°</span>` +
        ` <span style="color:#66ff66">Y:${s.restDeg[1]}°</span>` +
        ` <span style="color:#6688ff">Z:${s.restDeg[2]}°</span>`
      : null,
    s.twist
      ? `<span style="color:#e8a030">Twist  </span>` +
        `<span style="color:#ccc">bone:${s.twist.bone}°</span> ` +
        `<span style="color:#999">parent:${s.twist.parent}°</span> ` +
        `<span style="color:#ff8844">Δ${s.twist.delta}°</span>`
      : null,
    s.cone != null
      ? `<span style="color:#e8a030">Cone   </span>` +
        `<span style="color:#ccc">${s.cone}°</span>`
      : null,
    s.saturation
      ? `<span style="color:#e8a030">Limits </span>` +
        `<span style="color:${satColor(s.saturation)}">${s.saturation}</span>`
      : null,
    s.symmetry
      ? `<span style="color:#e8a030">Sym Δ  </span>` +
        `<span style="color:#ff6666">X:${s.symmetry.deltaXYZ[0]}°</span> ` +
        `<span style="color:#66ff66">Y:${s.symmetry.deltaXYZ[1]}°</span> ` +
        `<span style="color:#6688ff">Z:${s.symmetry.deltaXYZ[2]}°</span> ` +
        `<span style="color:#ff8844">Σ${s.symmetry.total}°</span>` +
        `<span style="color:#999"> vs ${s.symmetry.mirror}</span>`
      : null,
    `<span style="color:#999">Quat: [${s.quat.join(", ")}]</span>`,
    `<span style="color:#999">Stored: ${s.hasStoredPose ? "yes" : "rest pose"}</span>`,
    `<span style="color:#999">LocalX: [${s.localX.join(", ")}]</span>`,
    `<span style="color:#999">LocalY: [${s.localY.join(", ")}]</span>`,
    `<span style="color:#999">LocalZ: [${s.localZ.join(", ")}]</span>`,
  ]
    .filter(Boolean)
    .join("\n");
}
